import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { amount } from "@/lib/books";

/**
 * One section of a statement (P&L, balance sheet) as Tally nests it: groups
 * that fold open, ledgers that drill down to their statement for the period.
 * `linkParams` carries the period into the ledger statement.
 */
export function TreeRows({ node, depth = 0, linkParams = "", openDepth = 1, sign = 1 }) {
  const [open, setOpen] = useState(depth < openDepth);
  if (node.type === "ledger") {
    return (
      <tr className="hover:bg-blue-50/40">
        <td className="py-1.5 pr-4" style={{ paddingLeft: 16 + depth * 18 }}>
          <Link to={`/accounts/${node.guid}${linkParams}`} className="text-blue-700 hover:underline">{node.name}</Link>
        </td>
        <td className="py-1.5 pr-4 text-right tabular-nums">{amount(sign * node.amount)}</td>
      </tr>
    );
  }
  const kids = node.children || [];
  return (
    <>
      <tr className="hover:bg-slate-50">
        <td className="py-1.5 pr-4" style={{ paddingLeft: 16 + depth * 18 }}>
          {kids.length ? (
            <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 font-medium text-slate-800">
              {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
              {node.name}
            </button>
          ) : <span className="pl-[18px] font-medium text-slate-800">{node.name}</span>}
        </td>
        <td className="py-1.5 pr-4 text-right font-medium tabular-nums">{open && kids.length ? "" : amount(sign * node.amount)}</td>
      </tr>
      {open && kids.map((child) => (
        <TreeRows key={`${child.type}-${child.guid || child.name}`} node={child} depth={depth + 1} linkParams={linkParams}
          openDepth={openDepth} sign={sign} />
      ))}
      {open && kids.length > 0 && (
        <tr>
          <td className="py-1.5 pr-4 text-slate-600" style={{ paddingLeft: 16 + depth * 18 + 18 }}>Total for {node.name}</td>
          <td className="border-t border-slate-200 py-1.5 pr-4 text-right font-medium tabular-nums">{amount(sign * node.amount)}</td>
        </tr>
      )}
    </>
  );
}

/** A bold result line: Gross Profit, Net Profit, Total for Assets… */
export function TotalRow({ label, value, strong = true, muted = false }) {
  return (
    <tr className={`${strong ? "border-y border-slate-200 bg-slate-50/80" : ""}`}>
      <td className={`px-4 py-2 ${strong ? "font-semibold" : ""} ${muted ? "text-slate-600" : ""}`}>{label}</td>
      <td className={`px-4 py-2 text-right tabular-nums ${strong ? "font-semibold" : ""}`}>{amount(value)}</td>
    </tr>
  );
}

/** A section heading inside a statement table. */
export function HeadRow({ label }) {
  return (
    <tr>
      <td colSpan={2} className="px-4 pb-1 pt-4 text-[13px] font-semibold uppercase tracking-wide text-slate-500">{label}</td>
    </tr>
  );
}

/** A statement table shell: ACCOUNT | TOTAL header. */
export function StatementTable({ children, title, subtitle, company }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 text-center">
        {company && <div className="text-sm text-slate-500">{company}</div>}
        <div className="text-xl font-semibold text-slate-900">{title}</div>
        {subtitle && <div className="mt-1 text-sm text-slate-600">{subtitle}</div>}
      </div>
      <div className="overflow-x-auto px-2 pb-4 sm:px-8">
        <table className="mx-auto w-full max-w-4xl text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2.5 text-left">Account</th>
              <th className="px-4 py-2.5 text-right">Total</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}
